import { useState } from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { ar, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Box, Divider, IconButton, Modal, Skeleton, Typography } from "@mui/material";

import { Transaction } from "../../../api/types/transactions.types";
import { CurrencyEnum } from "../../../../../enums/currencies.enum";
import { useDataFormatter } from "../../../../../hooks/useDataFormatter";
import TransactionFigure from "../../../assets/images/transactions-modal.png";
import { useGetTransactionDetails } from "../../../api/queries/useGetTransactionDetails";
import { TransactionPaymentDestinationEnum, TransactionStatusEnum, TransactionTypeEnum } from "../../../enums/payment";
import {
  CloseButtonIcon,
  TransactionFromIcon,
  TransactionToIcon,
} from "../../../assets/icons/transactions-modals-icons";

import { formatCardNumber } from "./utils";

const amountFormatter = (amount: number, currency: CurrencyEnum, locale: string = "en") => {
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount / 100);
};

export const TransactionModal = ({
  transaction,
  open,
  closeModal,
}: {
  transaction?: Transaction;
  open: boolean;
  closeModal: () => void;
}) => {
  const { t, i18n } = useTranslation("plans", { keyPrefix: "component.transactionModal" });
  const { t: tEnum } = useTranslation("plans", { keyPrefix: "enum" });

  const { data: transactionDetails, isLoading } = useGetTransactionDetails(transaction);
  const details = transactionDetails?.data;

  const dateLocale = i18n.language === "ar" ? ar : enUS;
  const formattedAmount = details ? amountFormatter(details.amount, details.currency, i18n.language) : "";
  const formattedDate = details
    ? format(new Date(details.createdAt), "MMM dd, yyyy hh:mm a", { locale: dateLocale })
    : "";
  const transactionTypeText = transaction?.type === TransactionTypeEnum.Paid ? t("sendingMoney") : t("receivingMoney");

  const { fullName, profileLink } = useDataFormatter(details?.user);

  if (isLoading) {
    return (
      <Modal open={open} onClose={closeModal}>
        <Box sx={styles.modalContainer}>
          <Box sx={styles.modalContent}>
            <Box sx={styles.modalHeader}>
              <IconButton size="small" onClick={closeModal}>
                <CloseButtonIcon />
              </IconButton>
            </Box>
            <Divider flexItem orientation="horizontal" />
            <Box sx={styles.modalContent}>
              <Skeleton variant="rectangular" width={157} height={120} sx={{ borderRadius: "12px" }} />
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="text" width={150} height={24} />
            </Box>
          </Box>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={closeModal}>
      <Box sx={styles.modalContainer}>
        <Box sx={styles.modalContent}>
          <Box sx={styles.modalHeader}>
            <IconButton size="small" onClick={closeModal}>
              <CloseButtonIcon />
            </IconButton>
          </Box>
          <Divider flexItem orientation="horizontal" />
          <Box sx={styles.modalContent}>
            <Box sx={styles.transactionContainer}>
              <Box component="img" src={TransactionFigure} alt="Transaction" sx={styles.transactionFigure} />
              <Typography sx={styles.transactionAmount}>{formattedAmount || "-"}</Typography>
              <Typography sx={styles.transactionType}>{transactionTypeText}</Typography>
            </Box>
            <Box sx={styles.transactionInfoContainer}>
              <Typography sx={styles.infoLabel}>{t("transaction", { id: transaction?.id })}</Typography>
              {details?.user && (
                <Box sx={{ ...styles.infoCard, ...styles.fromCard }}>
                  {transaction?.type === TransactionTypeEnum.Paid ? <TransactionToIcon /> : <TransactionFromIcon />}
                  <Box sx={styles.fromCardContent}>
                    <Typography sx={styles.infoLabel}>
                      {transaction?.type === TransactionTypeEnum.Paid ? t("to") : t("from")}
                    </Typography>
                    <Typography sx={styles.transactionFromName} component={Link} to={profileLink}>
                      {fullName}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box sx={{ ...styles.infoCard, ...styles.infoCardColumns }}>
                <Box sx={styles.infoCardRow}>
                  <Typography sx={styles.infoLabel}>{t("reference")}</Typography>
                  <Typography sx={styles.infoValue}>{details?.stripePaymentIntentId || details?.id || "-"}</Typography>
                </Box>
                <Box sx={styles.infoCardRow}>
                  <Typography sx={styles.infoLabel}>{t("date")}</Typography>
                  <Typography sx={styles.infoValue}>{formattedDate || "-"}</Typography>
                </Box>
                <Box sx={styles.infoCardRow}>
                  <Typography sx={styles.infoLabel}>{t("card")}</Typography>
                  <Typography sx={styles.infoValue}>{formatCardNumber(details?.cardNumber) || "-"}</Typography>
                </Box>
                <Box sx={styles.infoCardRow}>
                  <Typography sx={styles.infoLabel}>{t("reasonOfPayment")}</Typography>
                  <Typography sx={styles.infoValue}>
                    {details?.type ? tEnum(`transaction_payment_destination.${details.type}`) : "--"}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ ...styles.infoCard, ...styles.infoCardColumns }}>
                <Box sx={styles.infoCardRow}>
                  <Typography sx={styles.infoLabel}>{t("amount")}</Typography>
                  <Typography sx={styles.infoValue}>{formattedAmount || "-"}</Typography>
                </Box>
                <Box sx={styles.infoCardRow}>
                  <Typography sx={styles.infoLabel}>{t("serviceFee")}</Typography>
                  <Typography sx={styles.infoValue}>-</Typography>
                </Box>
                <Divider flexItem orientation="horizontal" />
                <Box sx={styles.infoCardRow}>
                  <Typography sx={styles.infoLabel}>{t("totalAmount")}</Typography>
                  <Typography sx={styles.infoValue}>{formattedAmount || "-"}</Typography>
                </Box>
              </Box>
              <Box sx={{ ...styles.infoCard, ...styles.infoCardColumns }}>
                <Box sx={styles.infoCardRow}>
                  <Typography sx={styles.infoLabel}>{t("status")}</Typography>
                  <Typography sx={styles.statusChip(details?.status === TransactionStatusEnum.Success)}>
                    {details?.status ? tEnum(`transaction_status.${details.status}`) : "--"}
                  </Typography>
                </Box>
                <Box sx={styles.infoCardRow}>
                  <Typography sx={styles.infoLabel}>{t("category")}</Typography>
                  <Typography
                    sx={styles.categoryChip(
                      details?.type ? tEnum(`transaction_payment_destination.${details.type}`) : "--"
                    )}
                  >
                    {details?.type ? tEnum(`transaction_payment_destination.${details.type}`) : "--"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export const useTransactionModal = () => {
  const [open, setOpen] = useState(false);
  const [transaction, setTransaction] = useState<Transaction | undefined>(undefined);

  const handleOpenModal = (transaction: Transaction) => {
    setOpen(true);
    setTransaction(transaction);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setTransaction(undefined);
  };
  return { open, transaction, handleOpenModal, handleCloseModal };
};

const styles = {
  modalContainer: {
    width: "512px",
    height: "fit-content",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "16px",
    background: "#FFF",
    boxShadow: "0px 4px 50px 0px rgba(0, 0, 0, 0.08), 0px 4px 6px 0px rgba(0, 0, 0, 0.05)",
  },
  modalHeader: {
    width: "100%",
    display: "flex",
    padding: "24px 32px 0",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  modalContent: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  transactionContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
  },
  transactionFigure: {
    width: "157px",
    height: "120px",
  },
  transactionAmount: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "32px",
  },
  transactionType: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  transactionInfoContainer: {
    width: "100%",
    padding: "0 32px 32px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  infoCard: {
    borderRadius: "12px",
    background: "var(--Gray---25, #F7F8F8)",
    padding: "16px",
  },
  infoLabel: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  infoValue: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  fromCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
  },
  fromCardContent: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  infoCardColumns: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  infoCardRow: {
    display: "flex",
    justifyContent: "space-between",
  },
  transactionFromName: {
    textDecoration: "none",
    color: "var(--nutral-90, #262626)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
  statusChip: (isSuccess: boolean) => ({
    width: "fit-content",
    background: isSuccess ? "#ECFDF3" : "#FEF3F2",
    color: isSuccess ? "#027A48" : "#B42318",
    display: "flex",
    padding: "2px 8px 2px 6px",
    alignItems: "center",
    gap: "4px",
    borderRadius: "16px",
    mixBlendMode: "multiply",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  }),
  categoryChip: (category: TransactionPaymentDestinationEnum) => ({
    position: "relative",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    borderRadius: "16px",
    padding: "2px 8px 2px 18px",
    mixBlendMode: "multiply",

    background: "var(--Gray-100, #F2F4F7)",
    color: "var(--Gray-700, #344054)",
    "&::before": {
      content: '""',
      position: "absolute",
      left: "8px",
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 10,
      display: "block",
      width: "6px",
      height: "6px",
      borderRadius: "50%",
      background: "var(--Gray-700, #344054)",
    },
  }),
};
